import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './App.less';
import { createRoot } from 'react-dom/client';
import ThemeBanner from './Components/ThemeBanner.jsx';
import Themes from './Components/Themes.jsx';
import Loader from '../../_SharedComponents/Loader.jsx';


export default function App() {
    const config = require('Config');
    const appRef = useRef(null);

    const [state, setState] = useState({
        games: null,
        brands: null,
        tags: null,
        categories: null,
        sortOptions: null,
        session: null,
        themes: null,
    });

    const [selectedTheme, setSelectedTheme] = useState(null);
    const [startLazyImages, setStartLazyImages] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDesktop, setIsDesktop] = useState(new URL(window.location.href).searchParams.get("isDesktop") || false);
    const theme = useMemo(() => state.themes?.find(t => t.code === selectedTheme), [state.themes, selectedTheme]);

    useEffect(() => {
        setIsLoading(true);
        fetchThemesData();
        fetchGamesData(isDesktop);
        fetchSessionData();

        window.addEventListener("message", receiveMessages);
        window.addEventListener("resize", sendHeightFn);

        return () => {
            window.removeEventListener("resize", sendHeightFn);
            window.removeEventListener("message", receiveMessages);
        }
    }, [isDesktop]);

    useEffect(() => {
        if (startLazyImages) {
            casinoModule.utils.lazyImages.mountEventLazyLoadImages();
            setStartLazyImages(false);
        }
    }, [startLazyImages]);

    const sendThemeFn = (theme) => {
        setSelectedTheme(theme)
        window.parent.postMessage({ type: "theme", theme: theme }, "*");
        setTimeout(() => sendHeightFn (), 0)
    }

    const fetchThemesData = useCallback(async () => {
        try {
            const themesData = await window.casinoModule.repos.getThemes(config.restApiUrl, config.apiKey);
            casinoModule.constants.themes = themesData;

            setState(prevState => ({
                ...prevState,
                themes: themesData
            }));

            setIsLoading(false);
            setTimeout(() => sendHeightFn(), 0)
        } catch (error) {
            console.error('Error fetching themes:', error);
        }
    }, [config.restApiUrl, config.apiKey]);

    const fetchGamesData = useCallback(async () => {
        try {
            const [gamesData, categoriesData] = await Promise.all([
                window.casinoModule.repos.getGames(config.restApiUrl, config.apiKey, isDesktop),
                window.casinoModule.repos.getCategories(config.restApiUrl, config.apiKey)
            ]);

            casinoModule.constants.games = gamesData.items;
            casinoModule.constants.brands = gamesData.brendovi;
            casinoModule.constants.cache = gamesData.guiCacheVersion;
            casinoModule.constants.cdn = gamesData.cdnPath;
            casinoModule.constants.categories = categoriesData.items;

            setState(prevState => ({
                ...prevState,
                games: gamesData.items,
                brands: gamesData.brendovi,
                tags: gamesData.tagovi,
                categories: categoriesData.items,
                sortOptions: categoriesData.sortOptions
            }));

            setStartLazyImages(true);
            updateGamesHeight();
        } catch (error) {
            console.error('Error fetching games and categories:', error);
        }
    }, [config.restApiUrl, config.apiKey, isDesktop]);

    const fetchSessionData = useCallback(async () => {
        try {
            const sessionData = await window.casinoModule.repos.getSession(config.restApiUrl, config.apiKey);
            setState(prevState => ({
                ...prevState,
                session: sessionData,
            }));
        } catch (error) {
            console.error('Error fetching session data:', error);
        }
    }, [config.restApiUrl, config.apiKey]);

    const sendHeightFn = useCallback(() => {
        if (!appRef.current) return;
        const scrollHeight = appRef.current.scrollHeight;

        setTimeout(() => {
            window.parent.postMessage({ type: "themesHeight", scrollHeight: scrollHeight }, "*");
        }, 150);
    }, []);

    const receiveMessages = (event) => {
        switch (event.data.type) {
            case "userStateChanged":
                fetchSessionData();
                break;
            case "theme":
                setSelectedTheme(event.data.theme);
                setTimeout(() => sendHeightFn(), 0)
                break;
            default:
                break;
        }
    };

    const updateGamesHeight = useCallback(() => {
        setTimeout(() => {
            const scrollHeight = appRef.current?.scrollHeight;
            const gamesContainer = document.querySelector('.games-container');
            const gamesHeight = gamesContainer ? gamesContainer.scrollHeight : 0;
            const updatedScrollHeight = scrollHeight + gamesHeight;
            window.parent.postMessage({ type: "themesHeight", scrollHeight: updatedScrollHeight }, "*");
        }, 150);
    }, []);

    if (isLoading) return <Loader />;

    return (
        <div className={`themes-page-container ${!theme ? "" : "theme"}`} ref={appRef}>
            <ThemeBanner theme={theme} isDesktop={isDesktop} />
            <div className="themes-page">
                <Themes
                    sendHeightFn={sendHeightFn}
                    theme={theme}
                    themes={state.themes}
                    games={state.games}
                    brands={state.brands}
                    tags={state.tags}
                    categories={state.categories}
                    sortOptions={state.sortOptions}
                    session={state.session}
                    isDesktop={isDesktop}
                    sendThemeFn={sendThemeFn}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}

const domNode = document.getElementById('app');
const root = createRoot(domNode);

if (domNode) root.render(<App />);